if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open', 'data-webhook-url']);
      }

      constructor() {
        super();
        this.popupState = 'initial';
        this.successStateCookieName = 'sleektheme:popup:success';
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        this.webhookUrl = this.dataset.webhookUrl;
        this.cookieName = 'sleektheme:popup';

        // Check if user has already subscribed
        const successData = FoxTheme.utils.getStorage(this.successStateCookieName);
        if (successData && successData.subscribed) {
          this.popupState = 'success';
          this.updatePopupState();
        }

        window.FoxTheme.DOMready(this.handleOpen.bind(this));

        // Handle form submission
        window.FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '.newsletter-popup-form',
          event: 'submit',
          handler: this.handleFormSubmit.bind(this),
        });

        // Handle discount code copy
        window.FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '.btn-apply-coupon-code',
          handler: async (e, target) => {
            e.preventDefault();
            const code = target.dataset.code;
            if (code) this.copyAndApplyDiscountCode(code, target);
          },
        });
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();
        this.updatePopupState();
      }

      handleOpen() {
        if (Shopify.designMode) return;

        let savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.opened) {
          return false;
        }

        switch (this.triggerOpen) {
          case 'delay':
            setTimeout(() => {
              this.show();
            }, 5000);
            break;
          case 'scroll_down':
            this.triggerAfterScroll(700, this.show.bind(this));
            break;
          default:
            this.show();
            break;
        }
      }

      triggerAfterScroll(offset = 500, trigger) {
        let opened = false;
        const start = 0;
        window.addEventListener('scroll', (e) => {
          let scrollTop =
            window.pageYOffset || (document.documentElement || document.body.parentNode || document.body).scrollTop;

          if (scrollTop - start > offset && !opened) {
            trigger();
            opened = true;
            return;
          }
        });
      }

      async handleFormSubmit(e, form) {
        e.preventDefault();
        
        const emailInput = form.querySelector('input[type="email"]');
        const submitButton = form.querySelector('button[type="submit"]');
        const email = emailInput.value;

        // Validate email
        if (!this.validateEmail(email)) {
          emailInput.setCustomValidity('Please enter a valid email address');
          emailInput.reportValidity();
          return;
        }

        // Disable form while submitting
        emailInput.disabled = true;
        submitButton.disabled = true;
        submitButton.classList.add('loading');

        // Send to webhook
        try {
          fetch(this.webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: email }),
          }).catch(() => {
            // Silently fail - we don't care about the response
          });
        } catch (error) {
          // Silently fail
        }

        // Add delay to make it feel natural
        await new Promise(resolve => setTimeout(resolve, 800));

        // Switch to success state
        this.popupState = 'success';
        this.updatePopupState();

        // Save success state
        window.FoxTheme.utils.setStorage(this.successStateCookieName, { subscribed: true }, 365);

        // Re-enable form
        emailInput.disabled = false;
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
        
        // Clear form
        form.reset();
      }

      validateEmail(email) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
      }

      updatePopupState() {
        const elements = this.querySelectorAll('[data-popup-state]');
        
        elements.forEach(element => {
          const showIn = element.dataset.popupState;
          
          if (showIn === 'both') {
            element.style.display = '';
          } else if (showIn === this.popupState) {
            element.style.display = '';
          } else {
            element.style.display = 'none';
          }
        });
      }

      async copyAndApplyDiscountCode(code, button) {
        // Copy to clipboard
        navigator.clipboard.writeText(code);
        
        // Update button text
        const originalText = button.innerText;
        button.innerText = button.dataset.copiedText;
        
        // Apply discount code to cart
        try {
          const formData = new FormData();
          formData.append('form_type', 'cart');
          formData.append('updates[]', '0');
          formData.append('discount', code);
          
          await fetch('/cart/update.js', {
            method: 'POST',
            body: formData
          });

          // Also try to apply via checkout
          await fetch('/discount/' + code);
        } catch (error) {
          // Silently fail
        }

        // Restore button text after delay
        setTimeout(() => {
          button.innerText = originalText;
        }, 2000);
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}